import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "./store";

const hasConsent = (category: "analytics" | "marketing") => {
  const consent = window.ThemeConsent?.getConsent();
  return consent ? (consent as any)[category] : false;
};

export const analyticsMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action: any) => {
    // Capture state BEFORE the removal happens
    const prevState = store.getState();

    const result = next(action);

    if (action.type.endsWith("/fulfilled")) {
      const endpointName = action.meta?.arg?.endpointName;
      const responseData = action.payload; // This is the TransformedCart, TransformedProduct, or CheckoutData

      /*
       * NOTE ON PURCHASE TRACKING:
       * Purchase events are NOT handled in this middleware.
       *
       * Because purchase tracking requires verifying the Order ID against
       * session storage (to prevent double-counting on page refresh) and often
       * occurs after a payment gateway redirect, it is handled explicitly in:
       * `assets/src/js/templates/ecommerce/order/hooks/useOrderProcessing.ts`
       * via the `trackPurchase` helper.
       */

      // --- GA4 ---
      if (hasConsent("analytics") && typeof window.gtag === "function") {
        switch (endpointName) {
          case "getProductById":
          case "getProductBySlug":
            window.gtag("event", "view_item", {
              currency: responseData.currencyCode || "TRY",
              value: responseData.price,
              items: [
                {
                  item_id: String(responseData.id),
                  item_name: responseData.name,
                  price: responseData.price,
                },
              ],
            });
            break;

          case "addToCart":
            // Find the item that was just added (by ID from args)
            const addedItem = responseData.items.find(
              (i: any) => i.id === action.meta.arg.originalArgs.id
            );
            if (addedItem) {
              window.gtag("event", "add_to_cart", {
                currency: responseData.currencyCode || "TRY",
                value: addedItem.price,
                items: [
                  {
                    item_id: String(addedItem.id),
                    item_name: addedItem.name,
                    quantity: action.meta.arg.originalArgs.quantity,
                  },
                ],
              });
            }
            break;

          case "removeCartItem":
            const previousCart = prevState.api.queries["getCart(undefined)"]
              ?.data as any;

            const removedItem = previousCart?.items?.find(
              (i: any) => i.key === action.meta.arg.originalArgs.key
            );

            if (removedItem) {
              window.gtag("event", "remove_from_cart", {
                currency: previousCart?.currencyCode || "TRY",
                value: removedItem.price,
                items: [
                  {
                    item_id: String(removedItem.id),
                    item_name: removedItem.name,
                    quantity: removedItem.quantity,
                  },
                ],
              });
            }
            break;

          case "getCheckoutData":
            // GA4 Begin Checkout
            window.gtag("event", "begin_checkout", {
              currency: responseData.updatedCart?.currencyCode || "TRY",
              value: responseData.updatedCart?.totals?.totalPrice,
              items: responseData.updatedCart?.items?.map((item: any) => ({
                item_id: String(item.id),
                item_name: item.name,
                price: item.price,
                quantity: item.quantity,
              })),
            });
            break;
        }
      }

      // --- Meta Pixel ---
      if (hasConsent("marketing") && typeof window.fbq === "function") {
        switch (endpointName) {
          case "getProductById":
          case "getProductBySlug":
            window.fbq("track", "ViewContent", {
              content_name: responseData.name,
              content_ids: [String(responseData.id)],
              content_type: "product",
              value: responseData.price,
              currency: responseData.currencyCode || "TRY",
            });
            break;

          case "addToCart":
            window.fbq("track", "AddToCart", {
              content_ids: [String(action.meta.arg.originalArgs.id)],
              content_type: "product",
              currency: responseData.currencyCode || "TRY",
            });
            break;

          case "getCheckoutData":
            // Check if cart exists in the payload (it usually does in our transform logic)
            if (responseData.updatedCart) {
              window.fbq("track", "InitiateCheckout", {
                content_ids: responseData.updatedCart.items.map((i: any) =>
                  String(i.id)
                ),
                content_type: "product",
                value: responseData.updatedCart.totals.totalPrice,
                currency: responseData.updatedCart.currencyCode || "TRY",
                num_items: responseData.updatedCart.itemsCount,
              });
            }
            break;
        }
      }
    }

    return result;
  };
